import React, { useEffect, useState, createContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Login";
import LoginPage from "@/pages/LoginPage";
import ContactPage from "@/pages/ContactPage";
import Dashboard from "@/pages/Dashboard";
import TutorChat from "@/pages/TutorChat";
import RetoAvanzoDiagnostic from "@/pages/RetoAvanzoDiagnostic";
import RetoAvanzoProfile from "@/pages/RetoAvanzoProfile";
import LearningPaths from "@/pages/LearningPaths";
import Certifications from "@/pages/Certifications";
import TeacherDashboard from "@/pages/TeacherDashboard";
import SensitiveIncidents from "@/pages/SensitiveIncidents";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import InstitutionAdminDashboard from "@/pages/InstitutionAdminDashboard";
import B2cAdminDashboard from "@/pages/B2cAdminDashboard";
import B2cCompleteInvitePage from "@/pages/B2cCompleteInvitePage";
import InviteAccept from "@/pages/InviteAccept";
import ForgotPasswordB2C from "@/pages/ForgotPasswordB2C";
import ResetPasswordB2C from "@/pages/ResetPasswordB2C";
import SelectGrade from "@/pages/SelectGrade";
import Onboarding from "@/pages/Onboarding";
import Welcome from "@/pages/Welcome";
import ParentDashboard from "@/pages/ParentDashboard";
import PurchaseLanding, { PurchaseMockCheckout, PurchaseSuccess, PurchaseError } from "@/pages/Purchase";

// ── B2C (Preparación AVANZO) — isolated, uses its own guard ─────
import AvanzoLayout from "@/pages/avanzo/AvanzoLayout";
import AvanzoHome from "@/pages/avanzo/AvanzoHome";
import AvanzoTrain from "@/pages/avanzo/AvanzoTrain";
import AvanzoTrainV2 from "@/pages/avanzo/AvanzoTrainV2";
import BuddyTrainer from "@/pages/avanzo/BuddyTrainer";
import AvanzoPractice from "@/pages/avanzo/AvanzoPractice";
import AvanzoPracticeV2 from "@/pages/avanzo/AvanzoPracticeV2";

/**
 * Fase 6.1 cutover: `/avanzo/entrenar` y `/avanzo/practica` resuelven a v2
 * cuando REACT_APP_B2C_TRAIN_SELECTOR_V2 !== "false". El valor "false"
 * (o unset en preview con override) fuerza la experiencia legacy.
 * Las rutas explícitas `/entrenar-v2` y `/practica-v2` siempre son v2.
 */
const _V2_FLAG_RAW = process.env.REACT_APP_B2C_TRAIN_SELECTOR_V2;
const B2C_TRAIN_SELECTOR_V2 = _V2_FLAG_RAW !== "false"; // default ON
const AvanzoTrainRoute    = B2C_TRAIN_SELECTOR_V2 ? AvanzoTrainV2    : AvanzoTrain;
const AvanzoPracticeRoute = B2C_TRAIN_SELECTOR_V2 ? AvanzoPracticeV2 : AvanzoPractice;
import AvanzoProgress from "@/pages/avanzo/AvanzoProgress";
import AvanzoSimulation from "@/pages/avanzo/AvanzoSimulation";
import AvanzoSimulation1 from "@/pages/avanzo/AvanzoSimulation1";
import AvanzoProfile from "@/pages/avanzo/AvanzoProfile";
import AvanzoFamily from "@/pages/avanzo/AvanzoFamily";
import AvanzoProgressCheck from "@/pages/avanzo/AvanzoProgressCheck";
import AvanzoComprar from "@/pages/avanzo/AvanzoComprar";
import AvanzoPagoExitoso from "@/pages/avanzo/AvanzoPagoExitoso";
import AvanzoPagoCancelado from "@/pages/avanzo/AvanzoPagoCancelado";
import BuddyAvanzaLanding from "@/pages/avanzo/BuddyAvanzaLanding";

// ── Legal (FASE LEGAL-1) ────────────────────────────────────────
import BuddyAvanzaTerms from "@/pages/legal/BuddyAvanzaTerms";
import BuddyAvanzaPrivacy from "@/pages/legal/BuddyAvanzaPrivacy";
import BuddyAvanzaAUP from "@/pages/legal/BuddyAvanzaAUP";
import BuddyAvanzaAINotice from "@/pages/legal/BuddyAvanzaAINotice";
import BuddyAvanzaStudentPrivacy from "@/pages/legal/BuddyAvanzaStudentPrivacy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ── Silent pod warm-up ─────────────────────────────────────────────────
// Cold-start mitigation: fire a lightweight `GET /api/version` on the very
// first page load so the backend pod starts warming while the user is still
// reading the landing / typing credentials. Non-blocking, no toast, no
// visible side-effects. Idempotent — always safe to retry.
let __qhubWarmupPromise = null;
const warmupBackend = () => {
  if (__qhubWarmupPromise) return __qhubWarmupPromise;
  __qhubWarmupPromise = fetch(`${API}/version`, {
    method: "GET",
    credentials: "omit", // no cookies needed — pure warmup
    cache: "no-store",
    keepalive: true,     // survives page navigation
  }).catch(() => { /* swallow — this is best-effort */ });
  return __qhubWarmupPromise;
};
// Kick off warm-up as soon as the JS bundle evaluates (before React mounts)
warmupBackend();

// Auth Context
export const AuthContext = createContext(null);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Auth check failed:", error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#419cca] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#273043] font-medium">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router Component
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/es" replace />} />
      <Route path="/:lang" element={<Landing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/contacto" element={<ContactPage />} />
      <Route path="/comprar" element={<PurchaseLanding />} />
      <Route path="/comprar/mock-checkout" element={<PurchaseMockCheckout />} />
      <Route path="/avanzo/comprar" element={<AvanzoComprar />} />
      <Route path="/avanzo/pago-exitoso" element={<AvanzoPagoExitoso />} />
      <Route path="/avanzo/pago-cancelado" element={<AvanzoPagoCancelado />} />
      <Route path="/buddy-avanza" element={<BuddyAvanzaLanding />} />
      <Route path="/buddy-avanza/terminos" element={<BuddyAvanzaTerms />} />
      <Route path="/buddy-avanza/privacidad" element={<BuddyAvanzaPrivacy />} />
      <Route path="/buddy-avanza/uso-responsable" element={<BuddyAvanzaAUP />} />
      <Route path="/buddy-avanza/inteligencia-artificial" element={<BuddyAvanzaAINotice />} />
      <Route path="/buddy-avanza/privacidad-estudiantes" element={<BuddyAvanzaStudentPrivacy />} />
      <Route path="/comprar/exito" element={<PurchaseSuccess />} />
      <Route path="/comprar/error" element={<PurchaseError />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <Welcome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/select-grade"
        element={
          <ProtectedRoute>
            <SelectGrade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tutor/:subject"
        element={
          <ProtectedRoute>
            <TutorChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reto-avanzo/diagnostico"
        element={
          <ProtectedRoute>
            <RetoAvanzoDiagnostic />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reto-avanzo/perfil"
        element={
          <ProtectedRoute>
            <RetoAvanzoProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rutas/:subject"
        element={
          <ProtectedRoute>
            <LearningPaths />
          </ProtectedRoute>
        }
      />
      <Route
        path="/certificaciones"
        element={
          <ProtectedRoute>
            <Certifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teacher"
        element={
          <ProtectedRoute>
            <TeacherDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/incidencias-sensibles"
        element={
          <ProtectedRoute>
            <SensitiveIncidents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/institution-admin"
        element={
          <ProtectedRoute>
            <InstitutionAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/b2c"
        element={
          <ProtectedRoute>
            <B2cAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/b2c/complete-invite"
        element={<B2cCompleteInvitePage />}
      />
      <Route path="/invite" element={<InviteAccept />} />
      <Route path="/buddy-avanza/forgot-password" element={<ForgotPasswordB2C />} />
      <Route path="/reset-password" element={<ResetPasswordB2C />} />
      {/* ── Preparación AVANZO (B2C) — todas protegidas por B2CGuard ── */}
      <Route path="/avanzo" element={<AvanzoLayout />}>
        <Route index element={<AvanzoHome />} />
        <Route path="entrenar" element={<AvanzoTrainRoute />} />
        <Route path="entrenar-v2" element={<AvanzoTrainV2 />} />
        <Route path="practica" element={<AvanzoPracticeRoute />} />
        <Route path="practica-v2" element={<AvanzoPracticeV2 />} />
        <Route path="progreso" element={<AvanzoProgress />} />
        <Route path="control-progreso" element={<AvanzoProgressCheck />} />
        <Route path="simulacro-1" element={<AvanzoSimulation1 />} />
        <Route path="simulacro" element={<AvanzoSimulation />} />
        <Route path="perfil" element={<AvanzoProfile />} />
        <Route path="familia" element={<AvanzoFamily />} />
        <Route path="buddy" element={<BuddyTrainer />} />
      </Route>
      <Route
        path="/parent"
        element={
          <ProtectedRoute>
            <ParentDashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/es" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
